/**
 * SignalingProvider Abstraction Layer for Local Party Mode
 * Provides WebRTC signaling capabilities for peer-to-peer multiplayer
 */

// Callback types for type safety
export type PeerConnectedCallback = (peerId: string) => void;
export type PeerDisconnectedCallback = (peerId: string) => void;
export type MessageCallback = (peerId: string, data: unknown) => void;

/**
 * Abstract interface for signaling providers
 * Can be implemented by different signaling methods (PeerJS, WebSocket, etc.)
 */
export interface SignalingProvider {
    /**
     * Create a new room and become the host
     * @returns Promise resolving to the room code
     */
    createRoom(): Promise<string>;

    /**
     * Join an existing room as a client
     * @param roomCode The room code to join
     */
    joinRoom(roomCode: string): Promise<void>;

    /**
     * Register callback for when a peer connects
     * @param callback Function called with the peer's ID
     */
    onPeerConnected(callback: PeerConnectedCallback): void;

    /**
     * Register callback for when a peer disconnects
     * @param callback Function called with the peer's ID
     */
    onPeerDisconnected(callback: PeerDisconnectedCallback): void;

    /**
     * Register callback for incoming messages from peers
     * @param callback Function called with peer ID and message data
     */
    onMessage(callback: MessageCallback): void;

    /**
     * Send data to a specific peer
     * @param peerId Target peer's ID
     * @param data Data to send
     */
    sendToPeer(peerId: string, data: unknown): void;

    /**
     * Broadcast data to all connected peers
     * @param data Data to broadcast
     */
    broadcast(data: unknown): void;

    /**
     * Disconnect from the signaling service and all peers
     */
    disconnect(): void;

    /**
     * Get list of currently connected peer IDs
     * @returns Array of peer IDs
     */
    getConnectedPeers(): string[];

    /**
     * Check if this instance is the room host
     * @returns True if host, false if client
     */
    isHost(): boolean;
}

/**
 * Connection state for tracking peer status
 */
interface PeerConnection {
    id: string;
    connection: import('peerjs').DataConnection;
    isConnected: boolean;
    reconnectAttempts: number;
}

/**
 * PeerJS implementation of the SignalingProvider interface
 * Uses PeerJS for WebRTC signaling and data channels
 */
export class PeerJSSignalingProvider implements SignalingProvider {
    private static readonly MAX_PEERS = 16;
    private static readonly ROOM_CODE_LENGTH = 6;
    private static readonly MAX_RECONNECT_ATTEMPTS = 3;
    private static readonly RECONNECT_DELAY_MS = 2000;
    private static readonly ROOM_CODE_CHARS = 'ABCDEFGHJKLMNPQRSTUVWXYZ'; // Excluding I and O to avoid confusion

    private _peer: import('peerjs').Peer | null = null;
    private _connections: Map<string, PeerConnection> = new Map();
    private _roomCode: string | null = null;
    private _isHost: boolean = false;
    private _hostPeerId: string | null = null;

    // Callbacks
    private _onPeerConnectedCallback: PeerConnectedCallback | null = null;
    private _onPeerDisconnectedCallback: PeerDisconnectedCallback | null = null;
    private _onMessageCallback: MessageCallback | null = null;

    /**
     * Generate a random readable room code
     * @returns 6-character uppercase room code
     */
    private _generateRoomCode(): string {
        let code = '';
        for (let i = 0; i < PeerJSSignalingProvider.ROOM_CODE_LENGTH; i++) {
            const randomIndex = Math.floor(Math.random() * PeerJSSignalingProvider.ROOM_CODE_CHARS.length);
            code += PeerJSSignalingProvider.ROOM_CODE_CHARS[randomIndex];
        }
        return code;
    }

    /**
     * Convert room code to PeerJS peer ID format
     * @param roomCode The room code
     * @returns Formatted peer ID
     */
    private _roomCodeToPeerId(roomCode: string): string {
        return `wooorms-host-${roomCode.toUpperCase()}`;
    }

    /**
     * Initialize PeerJS with optional custom ID
     * @param peerId Optional peer ID (used for hosts)
     * @returns Promise resolving when peer is ready
     */
    private async _initializePeer(peerId?: string): Promise<import('peerjs').Peer> {
        const { Peer } = await import('peerjs');

        return new Promise((resolve, reject) => {
            const peer = peerId ? new Peer(peerId) : new Peer();

            peer.on('open', () => {
                resolve(peer);
            });

            peer.on('error', (error: Error & { type?: string }) => {
                console.error('[SignalingProvider] Peer error:', error);
                if (error.type === 'unavailable-id') {
                    reject(new Error('Room code already in use'));
                } else if (error.type === 'peer-unavailable') {
                    reject(new Error('Room not found'));
                } else {
                    reject(error);
                }
            });

            peer.on('disconnected', () => {
                console.warn('[SignalingProvider] Peer disconnected from server');
                // Attempt to reconnect
                if (!peer.destroyed) {
                    peer.reconnect();
                }
            });
        });
    }

    /**
     * Set up event handlers for a data connection
     * @param connection The PeerJS data connection
     */
    private _setupConnectionHandlers(connection: import('peerjs').DataConnection): void {
        const peerId = connection.peer;

        connection.on('open', () => {
            const peerConnection = this._connections.get(peerId);
            if (peerConnection) {
                peerConnection.isConnected = true;
                peerConnection.reconnectAttempts = 0;
            }

            if (this._onPeerConnectedCallback) {
                this._onPeerConnectedCallback(peerId);
            }
        });

        connection.on('data', (data: unknown) => {
            if (this._onMessageCallback) {
                this._onMessageCallback(peerId, data);
            }
        });

        connection.on('close', () => {
            this._handlePeerDisconnection(peerId);
        });

        connection.on('error', (error: Error) => {
            console.error(`[SignalingProvider] Connection error with peer ${peerId}:`, error);
            this._handlePeerDisconnection(peerId);
        });
    }

    /**
     * Handle peer disconnection with reconnection logic
     * @param peerId The disconnected peer's ID
     */
    private _handlePeerDisconnection(peerId: string): void {
        const peerConnection = this._connections.get(peerId);

        if (!peerConnection) {
            return;
        }

        peerConnection.isConnected = false;

        // Attempt reconnection if we're the host and haven't exceeded attempts
        if (this._isHost && peerConnection.reconnectAttempts < PeerJSSignalingProvider.MAX_RECONNECT_ATTEMPTS) {
            peerConnection.reconnectAttempts++;

            setTimeout(() => {
                if (this._peer && !this._peer.destroyed) {
                    const newConnection = this._peer.connect(peerId, { reliable: true });
                    peerConnection.connection = newConnection;
                    this._setupConnectionHandlers(newConnection);
                }
            }, PeerJSSignalingProvider.RECONNECT_DELAY_MS);
        } else {
            // Remove the peer and notify
            this._connections.delete(peerId);

            if (this._onPeerDisconnectedCallback) {
                this._onPeerDisconnectedCallback(peerId);
            }
        }
    }

    /**
     * Create a new room and become the host
     * @returns Promise resolving to the room code
     */
    public async createRoom(): Promise<string> {
        if (this._peer) {
            this.disconnect();
        }

        this._roomCode = this._generateRoomCode();
        this._isHost = true;
        const hostPeerId = this._roomCodeToPeerId(this._roomCode);

        try {
            this._peer = await this._initializePeer(hostPeerId);
            this._hostPeerId = hostPeerId;

            // Listen for incoming connections
            this._peer.on('connection', (connection: import('peerjs').DataConnection) => {
                if (this._connections.size >= PeerJSSignalingProvider.MAX_PEERS) {
                    console.warn('[SignalingProvider] Max peers reached, rejecting connection');
                    connection.close();
                    return;
                }

                this._connections.set(connection.peer, {
                    id: connection.peer,
                    connection,
                    isConnected: false,
                    reconnectAttempts: 0
                });

                this._setupConnectionHandlers(connection);
            });

            return this._roomCode;
        } catch (error) {
            this._roomCode = null;
            this._isHost = false;
            throw error;
        }
    }

    /**
     * Join an existing room as a client
     * @param roomCode The room code to join
     */
    public async joinRoom(roomCode: string): Promise<void> {
        if (this._peer) {
            this.disconnect();
        }

        const normalizedCode = roomCode.toUpperCase().trim();
        this._roomCode = normalizedCode;
        this._isHost = false;
        this._hostPeerId = this._roomCodeToPeerId(normalizedCode);

        try {
            this._peer = await this._initializePeer();

            // Connect to the host
            const connection = this._peer.connect(this._hostPeerId, { reliable: true });

            this._connections.set(this._hostPeerId, {
                id: this._hostPeerId,
                connection,
                isConnected: false,
                reconnectAttempts: 0
            });

            this._setupConnectionHandlers(connection);

            // Wait for the connection to open
            await new Promise<void>((resolve, reject) => {
                const timeout = setTimeout(() => {
                    reject(new Error('Connection timeout'));
                }, 10000);

                connection.on('open', () => {
                    clearTimeout(timeout);
                    resolve();
                });

                connection.on('error', (error: Error) => {
                    clearTimeout(timeout);
                    reject(error);
                });
            });
        } catch (error) {
            this._roomCode = null;
            this._hostPeerId = null;
            throw error;
        }
    }

    /**
     * Register callback for when a peer connects
     * @param callback Function called with the peer's ID
     */
    public onPeerConnected(callback: PeerConnectedCallback): void {
        this._onPeerConnectedCallback = callback;
    }

    /**
     * Register callback for when a peer disconnects
     * @param callback Function called with the peer's ID
     */
    public onPeerDisconnected(callback: PeerDisconnectedCallback): void {
        this._onPeerDisconnectedCallback = callback;
    }

    /**
     * Register callback for incoming messages from peers
     * @param callback Function called with peer ID and message data
     */
    public onMessage(callback: MessageCallback): void {
        this._onMessageCallback = callback;
    }

    /**
     * Send data to a specific peer
     * @param peerId Target peer's ID
     * @param data Data to send
     */
    public sendToPeer(peerId: string, data: unknown): void {
        const peerConnection = this._connections.get(peerId);

        if (!peerConnection || !peerConnection.isConnected) {
            console.warn(`[SignalingProvider] Cannot send to peer ${peerId}: not connected`);
            return;
        }

        try {
            peerConnection.connection.send(data);
        } catch (error) {
            console.error(`[SignalingProvider] Error sending to peer ${peerId}:`, error);
        }
    }

    /**
     * Broadcast data to all connected peers
     * @param data Data to broadcast
     */
    public broadcast(data: unknown): void {
        for (const [peerId, peerConnection] of this._connections) {
            if (peerConnection.isConnected) {
                try {
                    peerConnection.connection.send(data);
                } catch (error) {
                    console.error(`[SignalingProvider] Error broadcasting to peer ${peerId}:`, error);
                }
            }
        }
    }

    /**
     * Disconnect from the signaling service and all peers
     */
    public disconnect(): void {
        // Close all connections
        for (const [_, peerConnection] of this._connections) {
            try {
                peerConnection.connection.close();
            } catch (error) {
                console.error('[SignalingProvider] Error closing connection:', error);
            }
        }
        this._connections.clear();

        // Destroy the peer
        if (this._peer) {
            try {
                this._peer.destroy();
            } catch (error) {
                console.error('[SignalingProvider] Error destroying peer:', error);
            }
            this._peer = null;
        }

        // Reset state
        this._roomCode = null;
        this._isHost = false;
        this._hostPeerId = null;
    }

    /**
     * Get list of currently connected peer IDs
     * @returns Array of peer IDs
     */
    public getConnectedPeers(): string[] {
        const connectedPeers: string[] = [];

        for (const [peerId, peerConnection] of this._connections) {
            if (peerConnection.isConnected) {
                connectedPeers.push(peerId);
            }
        }

        return connectedPeers;
    }

    /**
     * Check if this instance is the room host
     * @returns True if host, false if client
     */
    public isHost(): boolean {
        return this._isHost;
    }

    /**
     * Get the current room code
     * @returns Room code or null if not in a room
     */
    public getRoomCode(): string | null {
        return this._roomCode;
    }

    /**
     * Get the local peer ID
     * @returns Peer ID or null if not initialized
     */
    public getLocalPeerId(): string | null {
        return this._peer?.id ?? null;
    }
}

// Export default instance factory
export function createSignalingProvider(): SignalingProvider {
    return new PeerJSSignalingProvider();
}
